import React, { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import * as api from '@apis/chats-v2';
import { ChatMessage, ChatRoom } from '@models/chat';
import { Button, Input, Spin } from 'antd';
import ChatBubbleV2 from './ChatBubbleV2';
import { useAuthentication } from '@hooks/authentication';
import { PagenatedResponse } from '@models/common';
import { AxiosResponse } from 'axios';
import { User } from '@models/auth';
import moment from 'moment';

interface ChatPreviewV2Props {
  chatRoom: ChatRoom & { participantsWithInfo?: User[] };
}

const ChatPreviewV2: React.FC<ChatPreviewV2Props> = ({ chatRoom }) => {
  const { authStore } = useAuthentication();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const participantMap = new Map(chatRoom.participantsWithInfo?.map((p) => [p.id, p]));
  const prevScrollHeightRef = useRef<number>();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  }: UseInfiniteQueryResult<InfiniteData<AxiosResponse<PagenatedResponse<ChatMessage>>>> = useInfiniteQuery({
    queryKey: ['chat-messages', chatRoom._id],
    queryFn: ({ pageParam = 0 }) => api.getChatRoomMessages(chatRoom._id, { page: pageParam, size: 20 }),
    getNextPageParam: (lastPage) => {
      return lastPage.data.hasNext ? lastPage.data.page + 1 : undefined;
    },
    initialPageParam: 0,
  });

  useEffect(() => {
    if (data) {
      const allMessages = data.pages
        .flatMap((page: AxiosResponse<PagenatedResponse<ChatMessage>>) => page.data.contents)
        .reverse();
      setMessages(allMessages);
    }
  }, [data]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    if (prevScrollHeightRef.current !== undefined) {
      // 이전 메시지를 불러온 경우, 스크롤 위치 유지
      chatContainer.scrollTop = chatContainer.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = undefined; // 사용 후 초기화
    } else {
      // 초기 로드 또는 새 메시지 수신 시 맨 아래로 스크롤
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [messages]);

  const handleScroll = () => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer && chatContainer.scrollTop === 0 && hasNextPage && !isFetchingNextPage) {
      // 이전 페이지를 불러오기 직전의 scrollHeight 저장
      prevScrollHeightRef.current = chatContainer.scrollHeight;
      fetchNextPage();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div ref={chatContainerRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
        {isLoading && <Spin />}
        {isFetchingNextPage && <Spin />}
        {messages.map((msg, index) => {
          const sender = participantMap.get(msg.senderId);
          const prevMsg = index > 0 ? messages[index - 1] : null;
          const isDifferentUser = !prevMsg || prevMsg.senderId !== msg.senderId;
          const isNeedDateLine = !prevMsg || !moment(msg.createdAt).isSame(moment(prevMsg.createdAt), 'day');

          return (
            <ChatBubbleV2
              key={msg._id}
              message={msg}
              isMe={msg.senderId === authStore.user?.id}
              isDifferentUser={isDifferentUser}
              isNeedDateLine={isNeedDateLine}
              profileImage={sender?.profile?.imageUrl}
              senderName={sender?.profile?.name || sender?.profile?.nickname}
            />
          );
        })}
      </div>
      <div style={{ padding: '10px', borderTop: '1px solid #f0f0f0' }}>
        <Input.Group compact>
          <Input style={{ width: 'calc(100% - 70px)' }} placeholder="메시지 입력 (구현 예정)" />
          <Button type="primary">전송</Button>
        </Input.Group>
      </div>
    </div>
  );
};

export default ChatPreviewV2;
